import { z } from 'zod/v4';

import { CurrencyRuntype } from '../backendTypes';
import { AssemblyReferenceRuntype } from '../pcb/pcbBackendTypes';
import { CalculatedPriceTableRuntype } from '../price/priceBackendTypes';

// Runtypes for the Stackrate quotation service (scala-workspace/framework/quotation, served
// under /api/quotation). This is the PCB price-quotation domain — deliberately named
// `pcbQuotation` because the existing `quotation` resource is backend-core's RfQ quotation-file
// feature (/projects/:projectId/quotation), a different domain.
// They mirror the Lagom JSON wire shapes from the Scala sources:
//   framework/quotation/api/.../package.scala        (Quotation, QuotationItem, QuotationStatus, ...)
//   framework/quotation/api/.../QuotationOrigin.scala
//   framework/assembly/commons/.../AssemblyFeature.scala (AbstractAssemblyReference)
//   common/utils/.../common/Address.scala
// Scala `Option` fields are omitted when None, hence `.optional()`.
// Scala `Instant` fields serialize as ISO-8601 UTC strings (play-json default), hence `z.string()`.

// AbstractAssemblyReference is a sealed trait with Play's default `_type` discriminator.
// Reads are lenient: an object without `_type` is parsed as AssemblyReference — so the plain
// AssemblyReferenceRuntype (reused from the pcb resource) works for requests as-is.
export const SharedAssemblyReferenceRuntype = z.object({
    _type: z.literal('SharedAssemblyReference'),
    team: z.string(),
    id: z.string(),
    sharedAssembly: AssemblyReferenceRuntype,
});
export type SharedAssemblyReference = z.infer<typeof SharedAssemblyReferenceRuntype>;

export const AbstractAssemblyReferenceRuntype = z.union([SharedAssemblyReferenceRuntype, AssemblyReferenceRuntype]);
export type AbstractAssemblyReference = z.infer<typeof AbstractAssemblyReferenceRuntype>;

export const QuotationOriginRuntype = z.enum(['api', 'manual']);
export type QuotationOrigin = z.infer<typeof QuotationOriginRuntype>;

// Not an enum: a { name, date } pair. Known status names are draft, sent, won, lost,
// overdue, archived, deleted.
export const QuotationStatusRuntype = z.object({
    name: z.string(),
    date: z.string().optional(),
});
export type QuotationStatus = z.infer<typeof QuotationStatusRuntype>;

export const PcbQuotationAddressRuntype = z.object({
    id: z.string().optional(),
    alias: z.string().optional(),
    name: z.string().optional(),
    phone: z.string().optional(),
    company: z.string().optional(),
    street: z.string(),
    no: z.string(),
    apartment: z.string().optional(),
    district: z.string().optional(),
    city: z.string(),
    postal: z.string(),
    country: z.string().optional(),
    continent: z.string().optional(),
    billing: z.boolean(),
    shipping: z.boolean(),
    notes: z.string().optional(),
});
export type PcbQuotationAddress = z.infer<typeof PcbQuotationAddressRuntype>;

export const QuotationItemInfoRuntype = z.object({
    specification: z.string().optional(),
    supplier: z.string().optional(),
    panel: z.string().optional(),
    technology: z.string().optional(),
    delivery: z.number().optional(),
    quantity: z.number().optional(),
    panelQuantity: z.number().optional(),
    nre: z.number().optional(),
    unitPrice: z.number().optional(),
    userDelivery: z.number().optional(),
    userQuantity: z.number().optional(),
    userPanelQuantity: z.number().optional(),
    userNRE: z.number().optional(),
    userUnitPrice: z.number().optional(),
    userTotal: z.number().optional(),
    userMarkup: z.number().optional(),
    total: z.number().optional(),
    details: z.array(CalculatedPriceTableRuntype).optional(),
    currency: CurrencyRuntype.optional(),
});
export type QuotationItemInfo = z.infer<typeof QuotationItemInfoRuntype>;

export const QuotationItemRuntype = z.object({
    id: z.string(),
    assembly: AbstractAssemblyReferenceRuntype,
    name: z.string().optional(),
    description: z.string().optional(),
    info: QuotationItemInfoRuntype.optional(),
    won: z.boolean().optional(),
});
export type QuotationItem = z.infer<typeof QuotationItemRuntype>;

export const QuotationItemReferenceRuntype = z.object({
    id: z.string(),
    // Read-only resolved view of the item; ignored on writes.
    resolved: QuotationItemRuntype.optional(),
});
export type QuotationItemReference = z.infer<typeof QuotationItemReferenceRuntype>;

export const PcbQuotationRuntype = z.object({
    team: z.string(),
    assembly: AbstractAssemblyReferenceRuntype,
    quotationId: z.string(),
    customerId: z.string(),
    name: z.string(),
    externalID: z.string().optional(),
    description: z.string().optional(),
    requestID: z.string().optional(),
    requestBy: z.string().optional(),
    created: z.string(),
    creator: z.string(),
    assignee: z.string().optional(),
    status: QuotationStatusRuntype,
    contactId: z.string().optional(),
    billing: PcbQuotationAddressRuntype.optional(),
    shipping: PcbQuotationAddressRuntype.optional(),
    items: z.array(QuotationItemReferenceRuntype).optional(),
    publicNotes: z.string().optional(),
    validFor: z.number().optional(),
    requestDate: z.string().optional(),
    origin: QuotationOriginRuntype.optional(),
});
export type PcbQuotation = z.infer<typeof PcbQuotationRuntype>;

// Flat element of the /quotation/search response.
export const PcbQuotationListItemRuntype = z.object({
    team: z.string(),
    quotationId: z.string(),
    name: z.string(),
    origin: QuotationOriginRuntype,
    customerId: z.string(),
    status: QuotationStatusRuntype,
    created: z.string(),
    creator: z.string(),
});
export type PcbQuotationListItem = z.infer<typeof PcbQuotationListItemRuntype>;

// Request body of POST /quotation/quotations and PUT /quotation/quotations/:idOrName.
// Note: `items` are item UUIDs here, unlike Quotation.items (references).
export const PcbQuotationCreationRuntype = z.object({
    name: z.string().optional(),
    assembly: AbstractAssemblyReferenceRuntype,
    customerId: z.string(),
    externalID: z.string().optional(),
    description: z.string().optional(),
    requestID: z.string().optional(),
    requestBy: z.string().optional(),
    assignee: z.string().optional(),
    contactId: z.string().optional(),
    billing: PcbQuotationAddressRuntype.optional(),
    shipping: PcbQuotationAddressRuntype.optional(),
    publicNotes: z.string().optional(),
    validFor: z.number().optional(),
    items: z.array(z.string()).optional(),
    requestDate: z.string().optional(),
    origin: QuotationOriginRuntype.optional(),
});
export type PcbQuotationCreation = z.infer<typeof PcbQuotationCreationRuntype>;

export const QuotationItemCreationRuntype = z.object({
    assembly: AbstractAssemblyReferenceRuntype,
    name: z.string().optional(),
    description: z.string().optional(),
    info: QuotationItemInfoRuntype.optional(),
});
export type QuotationItemCreation = z.infer<typeof QuotationItemCreationRuntype>;
